import { useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { subscribeLocation, isNativeLocation } from '../lib/locationService';
import { hasNativeTracker } from '../lib/nativeTracker';
import { CONSENT_KEY, CONSENT_EVENT } from './NativeTrackerSetup';
import { Button } from './ui/button';
import { MapPin, ShieldCheck } from 'lucide-react';

/*
 * PresenceTracker — always-on location reporting for every signed-in app user
 * EXCEPT super_admins (they are the watchers, not the watched).
 *
 * It shares the single device watcher with TripTracker via locationService, so
 * no second foreground service is spawned. It posts a position to the server:
 *   • when the user has moved ≥ PRESENCE_MOVE_M since the last post, and
 *   • a guaranteed heartbeat every PRESENCE_HEARTBEAT_MS (even while still).
 *
 * On the native app it shows a one-time consent card before starting, so staff
 * are told their location is shared with the organisation.
 */

const PRESENCE_MOVE_M       = 50;
const PRESENCE_HEARTBEAT_MS = 5 * 60_000;
const MIN_POST_GAP_MS       = 20_000;      // never hammer the server faster than this
const REQUEST_POLL_MS       = 60_000;      // check for a Super Admin location request
const NATIVE = isNativeLocation();
const NATIVE_SERVICE = hasNativeTracker();   // the Android service does the reporting itself

const haversineM = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371008.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const getBattery = async () => {
  try {
    const b = await navigator.getBattery?.();
    return b ? Math.round(b.level * 100) : undefined;
  } catch { return undefined; }
};

export default function PresenceTracker() {
  const { user } = useAuth();
  const tracked = !!user && user.role !== 'super_admin' && user.role !== 'tv_display';

  const [needConsent, setNeedConsent] = useState(false);
  const unsubRef = useRef(null);
  const lastPostedRef = useRef(null);     // { lat, lng }
  const lastPostAtRef = useRef(0);
  const lastHeartbeatRef = useRef(0);
  const batteryRef = useRef(undefined);
  const lastRawRef = useRef(null);
  const answeringRef = useRef(false);

  const post = useCallback(async (p, heartbeat) => {
    try {
      await api.post('/location/ping', { points: [{ ...p, battery: batteryRef.current, heartbeat }] });
      lastPostedRef.current = { lat: p.lat, lng: p.lng };
      lastPostAtRef.current = Date.now();
      if (heartbeat) lastHeartbeatRef.current = Date.now();
    } catch { /* retry on the next point */ }
  }, []);

  const onPoint = useCallback((raw) => {
    const p = {
      lat: raw.lat, lng: raw.lng, accuracy: raw.accuracy, speed: raw.speed,
      heading: raw.heading, altitude: raw.altitude,
      timestamp: raw.timestamp || new Date().toISOString(),
    };
    lastRawRef.current = p;
    const now = Date.now();
    const prev = lastPostedRef.current;
    const dueHeartbeat = now - lastHeartbeatRef.current >= PRESENCE_HEARTBEAT_MS;
    const moved = !prev || haversineM(prev, p) >= PRESENCE_MOVE_M;
    if (!dueHeartbeat && !moved) return;
    if (!dueHeartbeat && now - lastPostAtRef.current < MIN_POST_GAP_MS) return;
    post(p, dueHeartbeat);
  }, [post]);

  // Answer a Super Admin request silently with a fresh fix (falls back to the last watcher point).
  const answerRequest = useCallback(async () => {
    if (answeringRef.current) return;
    answeringRef.current = true;
    try {
      const { data } = await api.get('/location/request/pending');
      const req = data?.request;
      if (!req) return;
      const fresh = await new Promise((resolve) => {
        if (!navigator.geolocation) { resolve(null); return; }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({
            lat: pos.coords.latitude, lng: pos.coords.longitude,
            accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : undefined,
            timestamp: new Date(pos.timestamp || Date.now()).toISOString(),
          }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
        );
      });
      const p = fresh || lastRawRef.current;
      if (!p) return;
      batteryRef.current = await getBattery();
      await api.post('/location/ping', { request_id: req.id, points: [{ ...p, battery: batteryRef.current }] });
      lastPostedRef.current = { lat: p.lat, lng: p.lng };
      lastPostAtRef.current = Date.now();
    } catch { /* try again on the next poll */ }
    finally { answeringRef.current = false; }
  }, []);

  // Consent gate (native only — the browser's own permission prompt covers web)
  useEffect(() => {
    if (!tracked) { setNeedConsent(false); return; }
    if (!NATIVE) { setNeedConsent(false); return; }
    let agreed = false;
    try { agreed = localStorage.getItem(CONSENT_KEY) === '1'; } catch { /* noop */ }
    setNeedConsent(!agreed);
  }, [tracked]);

  const agree = useCallback(async () => {
    try { localStorage.setItem(CONSENT_KEY, '1'); } catch { /* noop */ }
    try { await api.post('/location/consent'); } catch { /* best effort */ }
    window.dispatchEvent(new Event(CONSENT_EVENT));
    setNeedConsent(false);
  }, []);

  // Start/stop the shared watcher subscription
  useEffect(() => {
    if (!tracked || needConsent || NATIVE_SERVICE) {
      if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
      return;
    }
    getBattery().then((b) => { batteryRef.current = b; });
    const heartbeatTimer = setInterval(() => { lastHeartbeatRef.current = 0; }, PRESENCE_HEARTBEAT_MS);
    const requestTimer = setInterval(answerRequest, REQUEST_POLL_MS);
    answerRequest();
    unsubRef.current = subscribeLocation({ distanceFilter: 50, onPoint });
    return () => {
      clearInterval(heartbeatTimer);
      clearInterval(requestTimer);
      if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    };
  }, [tracked, needConsent, onPoint, answerRequest]);

  if (!tracked || !needConsent) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="presence-consent">
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-sky-500/15 text-sky-500 flex items-center justify-center shrink-0">
            <MapPin size={22}/>
          </div>
          <div>
            <h2 className="text-lg font-semibold leading-tight">Location sharing</h2>
            <p className="text-xs text-muted-foreground">Required by {user?.name ? 'your organisation' : 'Sahakar Group'}</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          While you are signed in, this app shares your device location with your organisation's
          administrators — during work, on trips, and in the background. This helps verify field
          visits and trip reimbursements. Android will next ask you to <strong>Allow all the time</strong>;
          please accept so tracking keeps working when the screen is off.
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck size={14} className="text-emerald-500 shrink-0"/>
          Only a Super Admin can see your location. It is never shared with other staff.
        </div>
        <Button className="w-full" onClick={agree} data-testid="presence-consent-agree">
          I understand — enable location
        </Button>
      </div>
    </div>
  );
}
